/**
 * useScrollPosition Hook
 * V191: #129, #130 스크롤 위치 유지
 *
 * 목록 페이지에서 상세 보고 돌아올 때 스크롤 위치 기억
 * sessionStorage 사용으로 새로고침 시에도 유지
 */

import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface UseScrollPositionOptions {
  /**
   * 스크롤 위치를 저장할 키 (페이지별로 고유해야 함)
   */
  key: string;
  /**
   * 스크롤 대상 요소 (기본: window)
   */
  scrollRef?: React.RefObject<HTMLElement>;
  /**
   * 복원 지연 시간 (ms, 기본: 100)
   */
  restoreDelay?: number;
}

/**
 * 스크롤 위치를 sessionStorage에 저장/복원하는 훅
 *
 * @example
 * ```tsx
 * // 기본 사용 (window 스크롤)
 * useScrollPosition({ key: 'consult-list' });
 *
 * // 특정 요소 스크롤
 * const scrollRef = useRef<HTMLDivElement>(null);
 * useScrollPosition({ key: 'customer-list', scrollRef });
 * ```
 */
export function useScrollPosition({
  key,
  scrollRef,
  restoreDelay = 100,
}: UseScrollPositionOptions) {
  const location = useLocation();
  const storageKey = `scroll-position-${key}`;
  const isRestoringRef = useRef(false);

  // 메인 스크롤 컨테이너 (DashboardLayout의 <main>)
  const getMainScrollContainer = useCallback(() => {
    return document.getElementById('main-scroll-container');
  }, []);

  // 스크롤 위치 저장
  const saveScrollPosition = useCallback(() => {
    if (isRestoringRef.current) return;

    let scrollTop: number;
    if (scrollRef?.current) {
      scrollTop = scrollRef.current.scrollTop;
    } else {
      const mainEl = getMainScrollContainer();
      scrollTop = mainEl ? mainEl.scrollTop : window.scrollY;
    }

    sessionStorage.setItem(storageKey, String(scrollTop));
  }, [storageKey, scrollRef, getMainScrollContainer]);

  // 스크롤 위치 복원
  const restoreScrollPosition = useCallback(() => {
    const savedPosition = sessionStorage.getItem(storageKey);
    if (savedPosition === null) return;

    const position = parseInt(savedPosition, 10);
    if (isNaN(position)) return;

    isRestoringRef.current = true;

    // 지연 후 복원 (DOM 렌더링 완료 대기)
    setTimeout(() => {
      if (scrollRef?.current) {
        scrollRef.current.scrollTop = position;
      } else {
        const mainEl = getMainScrollContainer();
        if (mainEl) {
          mainEl.scrollTo(0, position);
        } else {
          window.scrollTo(0, position);
        }
      }
      isRestoringRef.current = false;
    }, restoreDelay);
  }, [storageKey, scrollRef, restoreDelay, getMainScrollContainer]);

  // 스크롤 위치 초기화
  const clearScrollPosition = useCallback(() => {
    sessionStorage.removeItem(storageKey);
  }, [storageKey]);

  // 마운트 시 스크롤 위치 복원
  useEffect(() => {
    restoreScrollPosition();
  }, [restoreScrollPosition]);

  // 스크롤 이벤트 리스너 등록
  useEffect(() => {
    const target = scrollRef?.current || getMainScrollContainer() || window;
    const handleScroll = () => saveScrollPosition();

    // 스로틀링 적용 (100ms)
    let timeoutId: NodeJS.Timeout | null = null;
    const throttledHandler = () => {
      if (timeoutId) return;
      timeoutId = setTimeout(() => {
        handleScroll();
        timeoutId = null;
      }, 100);
    };

    target.addEventListener('scroll', throttledHandler, { passive: true });

    return () => {
      target.removeEventListener('scroll', throttledHandler);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [saveScrollPosition, scrollRef, getMainScrollContainer]);

  // 경로 변경 시 (다른 페이지로 이동) 스크롤 위치 유지
  // 같은 페이지 내 쿼리스트링 변경은 유지
  useEffect(() => {
    return () => {
      // 언마운트 시 현재 스크롤 위치 저장
      saveScrollPosition();
    };
  }, [saveScrollPosition]);

  return {
    saveScrollPosition,
    restoreScrollPosition,
    clearScrollPosition,
  };
}

export default useScrollPosition;
